import {
  ProgressDataResponse,
  LLMToolCallBlock,
  LLMToolResultBlock,
  LLMConversationHistory,
  LLMMessageBlock,
  LLMMessage,
} from 'llm/deep-research/DeepResearchDAO';
import {ProgressData} from 'llm/deep-research/messages/DeepResearchProgress';
import {DeepResearchToolType, ProgressToolCall} from 'llm/deep-research/tool-details/types';
import {getToolCall} from 'llm/deep-research/tool-details/utils';

/**
 * Converts a ProgressDataResponse from the backend to the ProgressData format
 * expected by the frontend components.
 */
export function convertProgressDataResponseToProgressData(
  progressDataResponse: ProgressDataResponse
): ProgressData {
  const {chatMetadata, conversationHistories} = progressDataResponse;

  const childConversationIds = new Set(chatMetadata.rootNode.children.map((child) => child.conversationId));

  const childConversationHistories = Object.values(conversationHistories).filter((history) =>
    childConversationIds.has(history.conversationId)
  );
  const historyThread = getProgressToolCalls(childConversationHistories);

  // TODO (BNCH-126864): Add other sources to progress data
  const numSources = 0;

  return {
    historyThread,
    numSources,
  };
}

const toolsToInclude = [
  DeepResearchToolType.SearchTool,
  DeepResearchToolType.GetWarehouseOverviewTool,
  DeepResearchToolType.QueryWarehouseTool,
  DeepResearchToolType.NotebookQueryTool,
  DeepResearchToolType.AttachmentTool,
  DeepResearchToolType.ResearchSubAgent,
  DeepResearchToolType.Think,
  DeepResearchToolType.CompleteAnswer,
];

export function getProgressToolCalls(
  conversationHistories: Array<LLMConversationHistory>
): Array<ProgressToolCall> {
  // We assume that tool calls will always precede their corresponding tool results.
  const toolMap = new Map<string, [LLMToolCallBlock, LLMToolResultBlock | null, Date]>();

  conversationHistories.forEach((history) => {
    history.messages.forEach((message: LLMMessage) => {
      const createdAt = new Date(message.createdAt);

      message.messageBlocks.forEach((block: LLMMessageBlock) => {
        if (block.type === 'tool_call') {
          if (toolsToInclude.includes(block.value.name)) {
            toolMap.set(block.value.id, [block, null, createdAt]);
          }
        } else if (block.type === 'tool_result') {
          const existing = toolMap.get(block.value.id);
          if (existing) {
            const [toolCall, _, date] = existing;
            toolMap.set(block.value.id, [toolCall, block, date]);
          }
        }
      });
    });
  });

  // TODO (BNCH-126793): Add tool summarized tool calls to frontend
  return Array.from(toolMap.values())
    .map(([toolCall, toolResult, createdAt]) => getToolCall(toolCall, toolResult, createdAt))
    .filter((toolCall): toolCall is ProgressToolCall => toolCall != null)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}
